import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { 
  Search, ChevronLeft, ChevronRight, Home, FileText, 
  Clock, Calendar, Building2, ArrowRight, Megaphone,
  AlertCircle, CheckCircle, Info
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useIsMobile } from "../components/ui/use-mobile";

interface Announcement {
  id: number;
  projectNo: string;
  projectName: string;
  biddingUnit: string;
  announcementType: number;
  openBidTime: string | null;
  fileGetEndTime: string | null;
  publishTime: string;
  isHide?: number | null;
}

// 公告类型映射
const announcementTypes = [
  { id: 1, name: "招标（采购）公告", icon: Megaphone, color: "#3b82f6", bgColor: "#eff6ff" },
  { id: 2, name: "变更公告", icon: AlertCircle, color: "#f59e0b", bgColor: "#fffbeb" },
  { id: 3, name: "中标公告", icon: CheckCircle, color: "#10b981", bgColor: "#ecfdf5" },
  { id: 4, name: "其他公告", icon: Info, color: "#8b5cf6", bgColor: "#f5f3ff" },
];

export default function TradeAnnouncementsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const [hoveredTab, setHoveredTab] = useState<number | null>(null);
  const pageSize = 10;

  // 获取当前选中的公告类型配置
  const currentType = announcementTypes.find(t => t.id === activeTab) || announcementTypes[0];

  // 格式化日期
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${d} ${h}:${min}:${s}`;
  };

  // 获取公告列表
  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: activeTab.toString(),
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });
      
      if (searchKeyword) {
        params.append('keyword', searchKeyword);
      }

      const response = await fetch(`/api/public/trade-announcements?${params}`);
      const data = await response.json();

      if (data.ok) {
        const mapped = data.data.map((item: any) => ({
          id: item.id,
          projectNo: item.project_no,
          projectName: item.project_name,
          biddingUnit: item.bidding_unit,
          announcementType: item.announcement_type,
          openBidTime: formatDate(item.open_bid_time),
          fileGetEndTime: formatDate(item.file_get_end_time),
          publishTime: formatDate(item.publish_time) || '',
          isHide: item.is_hide ?? null,
        }));
        // 兜底：即使后端漏过滤，也不展示被隐藏的招标公告
        setAnnouncements(mapped.filter((a: Announcement) => !(a.announcementType === 1 && a.isHide === 1)));
        setTotal(data.total);
      }
    } catch (error) {
      console.error('获取公告列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 切换页签时重置页码
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchKeyword]);

  // 加载数据
  useEffect(() => {
    fetchAnnouncements();
  }, [activeTab, currentPage, searchKeyword]);

  // 分页数据
  const totalPages = Math.ceil(total / pageSize);

  // 处理查看详情
  const handleViewDetail = (announcement: Announcement) => {
    navigate(`/announcement-detail/${announcement.id}`);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />

      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #003c8c 0%, #0050b8 50%, #0066cc 100%)',
        padding: isMobile ? '32px 0' : '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰元素 */}
        <div style={{
          position: 'absolute',
          top: '-80px',
          right: '-50px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          left: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '15%',
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.02)',
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '12px' : '16px' }}>
            <FileText style={{ width: isMobile ? '28px' : '40px', height: isMobile ? '28px' : '40px', color: '#fbbf24' }} />
            <h1 style={{ 
              fontSize: isMobile ? '24px' : '36px', 
              fontWeight: 700, 
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              项目公告信息
            </h1>
          </div>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(255,255,255,0.8)', maxWidth: '600px' }}>
            为您提供实时、精准的项目招采公告信息服务
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '12px 0' : '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap' }}>
            <Link
              to="/"
              style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseOver={(e) => e.currentTarget.style.color = '#003c8c'}
              onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}
            >
              <Home style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: '#1f2937', fontWeight: 500 }}>项目公告信息</span>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: currentType.color, fontWeight: 500 }}>{currentType.name}</span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '20px 0' : '40px 0' }}>
        {/* 页签导航卡片 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? '12px' : '20px',
          marginBottom: isMobile ? '20px' : '32px'
        }}>
          {announcementTypes.map((type) => {
            const Icon = type.icon;
            const isActive = activeTab === type.id;
            const isHovered = hoveredTab === type.id;
            
            return (
              <button
                key={type.id}
                onClick={() => setActiveTab(type.id)}
                onMouseEnter={() => setHoveredTab(type.id)}
                onMouseLeave={() => setHoveredTab(null)}
                style={{
                  padding: isMobile ? '16px' : '24px',
                  borderRadius: isMobile ? '12px' : '16px',
                  border: 'none',
                  backgroundColor: isActive ? type.color : 'white',
                  boxShadow: isActive 
                    ? `0 10px 30px ${type.color}40`
                    : isHovered 
                      ? '0 10px 30px rgba(0,0,0,0.1)'
                      : '0 2px 8px rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: isActive || isHovered ? 'translateY(-4px)' : 'translateY(0)',
                  textAlign: 'left'
                }}
              >
                <div style={{
                  width: isMobile ? '36px' : '48px',
                  height: isMobile ? '36px' : '48px',
                  borderRadius: isMobile ? '10px' : '12px',
                  backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : type.bgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: isMobile ? '12px' : '16px',
                  transition: 'all 0.3s ease'
                }}>
                  <Icon style={{ 
                    width: isMobile ? '18px' : '24px', 
                    height: isMobile ? '18px' : '24px', 
                    color: isActive ? 'white' : type.color 
                  }} />
                </div>
                <div style={{ 
                  fontSize: isMobile ? '13px' : '16px', 
                  fontWeight: 600, 
                  color: isActive ? 'white' : '#1f2937',
                  marginBottom: '4px'
                }}>
                  {type.name}
                </div>
                {isActive && (
                  <div style={{ 
                    fontSize: isMobile ? '11px' : '13px', 
                    color: 'rgba(255,255,255,0.8)'
                  }}>
                    {total} 条公告
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* 搜索和列表区域 */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          {/* 搜索栏 */}
          <div style={{
            padding: isMobile ? '16px' : '24px 32px',
            borderBottom: '1px solid #f3f4f6',
            background: 'linear-gradient(to right, #fafbfc, white)'
          }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? '16px' : '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{
                  width: '4px',
                  height: isMobile ? '20px' : '24px',
                  backgroundColor: currentType.color,
                  borderRadius: '2px'
                }} />
                <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#1f2937' }}>
                  {currentType.name}列表
                </h3>
                <span style={{
                  padding: '4px 12px',
                  backgroundColor: currentType.bgColor,
                  color: currentType.color,
                  fontSize: isMobile ? '12px' : '13px',
                  fontWeight: 500,
                  borderRadius: '20px'
                }}>
                  共 {total} 条
                </span>
              </div>
              
              {/* 搜索框 */}
              <div style={{ position: 'relative', width: isMobile ? '100%' : '320px' }}>
                <input
                  type="text"
                  placeholder="搜索项目编号或项目名称..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  style={{
                    width: '100%',
                    height: isMobile ? '40px' : '44px',
                    paddingLeft: '48px',
                    paddingRight: '16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: isMobile ? '13px' : '14px',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = currentType.color;
                    e.currentTarget.style.boxShadow = `0 0 0 4px ${currentType.color}15`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                <Search style={{ 
                  position: 'absolute', 
                  left: '16px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  width: isMobile ? '18px' : '20px', 
                  height: isMobile ? '18px' : '20px', 
                  color: '#9ca3af' 
                }} />
              </div>
            </div>
          </div>

          {/* 公告列表 */}
          {loading ? (
            <div style={{ padding: '80px 0', textAlign: 'center', color: '#6b7280' }}>
              加载中...
            </div>
          ) : announcements.length > 0 ? (
            <div>
              {announcements.map((item, index) => (
                <div
                  key={item.id}
                  onClick={() => handleViewDetail(item)}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    padding: isMobile ? '16px' : '24px 32px',
                    cursor: 'pointer',
                    borderBottom: index < announcements.length - 1 ? '1px solid #f3f4f6' : 'none',
                    background: hoveredItem === item.id 
                      ? `linear-gradient(to right, ${currentType.bgColor}, transparent)` 
                      : 'white',
                    transform: hoveredItem === item.id ? 'scale(1.002)' : 'scale(1)',
                    transition: 'all 0.25s ease'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? '16px' : '24px' }}>
                    {/* 左侧图标 */}
                    <div style={{
                      width: isMobile ? '48px' : '56px',
                      height: isMobile ? '48px' : '56px',
                      borderRadius: isMobile ? '12px' : '14px',
                      background: hoveredItem === item.id 
                        ? `linear-gradient(135deg, ${currentType.color}, ${currentType.color}cc)`
                        : currentType.bgColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.3s ease',
                      alignSelf: isMobile ? 'flex-start' : 'auto'
                    }}>
                      <currentType.icon style={{ 
                        width: isMobile ? '22px' : '26px', 
                        height: isMobile ? '22px' : '26px', 
                        color: hoveredItem === item.id ? 'white' : currentType.color 
                      }} />
                    </div>

                    {/* 中间内容 */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ 
                        fontSize: isMobile ? '15px' : '17px', 
                        fontWeight: 600, 
                        color: hoveredItem === item.id ? currentType.color : '#1f2937', 
                        marginBottom: isMobile ? '8px' : '10px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobile ? 'normal' : 'nowrap',
                        transition: 'color 0.2s',
                        lineHeight: 1.4
                      }}>
                        {item.projectName}
                      </h3>
                      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '24px', fontSize: isMobile ? '12px' : '14px', color: '#6b7280' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Building2 style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px' }} />
                          {item.biddingUnit}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px' }} />
                          发布：{item.publishTime.split(' ')[0]}
                        </span>
                      </div>
                      {/* 移动端显示时间信息 */}
                      {isMobile && (
                        <div style={{ display: 'flex', gap: '16px', marginTop: '12px', fontSize: '12px' }}>
                          {item.openBidTime && (
                            <div>
                              <div style={{ color: '#9ca3af', marginBottom: '4px' }}>开标时间</div>
                              <div style={{ fontWeight: 500, color: '#374151' }}>
                                {item.openBidTime.split(' ')[0]}
                              </div>
                            </div>
                          )}
                          {item.fileGetEndTime && (
                            <div>
                              <div style={{ color: '#9ca3af', marginBottom: '4px' }}>文件截止</div>
                              <div style={{ fontWeight: 500, color: '#374151' }}>
                                {item.fileGetEndTime.split(' ')[0]}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 右侧时间信息 - 桌面端 */}
                    {!isMobile && (
                      <div style={{ 
                        flexShrink: 0, 
                        display: 'flex',
                        gap: '32px',
                        marginRight: '24px'
                      }}>
                        {item.openBidTime && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>开标时间</div>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                              {item.openBidTime.split(' ')[0]}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {item.openBidTime.split(' ')[1]}
                            </div>
                          </div>
                        )}
                        {item.fileGetEndTime && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>文件截止</div>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                              {item.fileGetEndTime.split(' ')[0]}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {item.fileGetEndTime.split(' ')[1]}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 查看详情按钮 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetail(item);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: isMobile ? '10px 16px' : '10px 20px',
                        border: `2px solid ${currentType.color}`,
                        borderRadius: '10px',
                        backgroundColor: hoveredItem === item.id ? currentType.color : 'white',
                        color: hoveredItem === item.id ? 'white' : currentType.color,
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                        width: isMobile ? '100%' : 'auto'
                      }}
                    >
                      查看详情
                      <ArrowRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '80px 0', textAlign: 'center' }}>
              <div style={{ 
                width: '100px', 
                height: '100px', 
                margin: '0 auto 24px', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Search style={{ width: '40px', height: '40px', color: '#9ca3af' }} />
              </div>
              <p style={{ color: '#6b7280', fontSize: '16px' }}>暂无相关公告</p>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginTop: '8px' }}>请尝试其他搜索条件</p>
            </div>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <div style={{ 
              padding: isMobile ? '16px' : '24px 32px', 
              borderTop: '1px solid #f3f4f6', 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center', 
              justifyContent: 'space-between',
              background: 'linear-gradient(to right, #fafbfc, white)',
              gap: isMobile ? '16px' : '0'
            }}>
              <span style={{ fontSize: isMobile ? '12px' : '14px', color: '#6b7280', textAlign: isMobile ? 'center' : 'left' }}>
                共 <span style={{ color: currentType.color, fontWeight: 600 }}>{total}</span> 条记录，
                第 <span style={{ fontWeight: 600 }}>{currentPage}</span>/<span style={{ fontWeight: 600 }}>{totalPages}</span> 页
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', justifyContent: isMobile ? 'center' : 'flex-start', flexWrap: 'wrap' }}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: currentPage === 1 ? '#d1d5db' : '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <ChevronLeft style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                  上一页
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      width: isMobile ? '32px' : '36px',
                      height: isMobile ? '32px' : '36px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: page === currentPage ? currentType.color : 'white',
                      color: page === currentPage ? 'white' : '#374151',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: page === currentPage ? 600 : 400,
                      cursor: 'pointer',
                      boxShadow: page === currentPage ? `0 4px 12px ${currentType.color}40` : '0 1px 3px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s'
                    }}
                  >
                    {page}
                  </button>
                ))}

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: currentPage === totalPages ? '#d1d5db' : '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  下一页
                  <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
